import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, BadgeCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiError } from "./api";
import { EASE } from "@/components/Motion";

// "Har login pe dikhe" — dismissal is kept per browser session only, so the
// popup returns on the next session while the offer is active (and unclaimed).
const seenKey = (o) => `bk_offer_seen_${o.id}_${o.updated_at || ""}`;

export default function OfferPopup({ user }) {
  const [offer, setOffer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [claimed, setClaimed] = useState(false);

  useEffect(() => {
    if (!user || user.role === "admin") return;
    api
      .get("/offers/active")
      .then(({ data }) => {
        const next = (data || []).find((o) => {
          // hide while a deal is in motion (pending/contacted); closed = admin may re-offer
          if (o.claim_status === "pending" || o.claim_status === "contacted") return false;
          try {
            if (sessionStorage.getItem(seenKey(o))) return false;
          } catch {}
          return true;
        });
        if (next) setOffer(next);
      })
      .catch(() => {});
  }, [user]);

  const close = () => {
    if (offer) {
      try {
        sessionStorage.setItem(seenKey(offer), "1");
      } catch {}
    }
    setOffer(null);
  };

  const claim = async () => {
    setBusy(true);
    try {
      await api.post(`/offers/${offer.id}/claim`);
      setClaimed(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const save =
    offer?.original_price && offer.original_price > offer.offer_price
      ? offer.original_price - offer.offer_price
      : null;

  return (
    <AnimatePresence>
      {offer && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-4 backdrop-blur-md"
          data-testid="offer-popup-overlay"
        >
          <div className="noise-overlay pointer-events-none absolute inset-0 opacity-[0.05]" />
          <motion.div
            initial={{ opacity: 0, y: 48, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 32, scale: 0.97 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-gold/30 bg-panel shadow-[0_30px_120px_rgb(var(--gold)_/_0.15)]"
            data-testid="offer-popup"
          >
            <button
              onClick={close}
              data-testid="offer-popup-close"
              aria-label="Close offer"
              className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-black/50 text-neutral-300 backdrop-blur transition-colors hover:border-gold/50 hover:text-goldlight"
            >
              <X size={16} />
            </button>

            {offer.image ? (
              <div className="relative h-56 overflow-hidden sm:h-64">
                <img src={offer.image} alt={offer.title} className="h-full w-full object-cover" data-testid="offer-popup-image" />
                <div className="absolute inset-0 bg-gradient-to-t from-[rgb(var(--panel))] via-transparent to-transparent" />
                {offer.badge && (
                  <span
                    className="absolute left-4 top-4 rounded-full bg-gradient-to-br from-goldlight via-gold to-golddark px-3.5 py-1.5 font-mono text-[10px] font-bold tracking-[0.2em] text-black"
                    data-testid="offer-popup-badge"
                  >
                    {offer.badge}
                  </span>
                )}
              </div>
            ) : (
              <div className="relative flex h-32 items-center justify-center overflow-hidden bg-gold/[0.06]">
                <Sparkles size={30} className="text-gold/50" />
                {offer.badge && (
                  <span
                    className="absolute left-4 top-4 rounded-full bg-gradient-to-br from-goldlight via-gold to-golddark px-3.5 py-1.5 font-mono text-[10px] font-bold tracking-[0.2em] text-black"
                    data-testid="offer-popup-badge"
                  >
                    {offer.badge}
                  </span>
                )}
              </div>
            )}

            <div className="px-7 pb-7 pt-5 sm:px-9 sm:pb-9">
              <div className="flex items-center gap-2 font-mono text-[9px] tracking-[0.3em] text-gold/80">
                <Sparkles size={11} /> PRIVATE DEAL — ONLY FOR YOU
              </div>
              <h2 className="mt-3 font-serif text-2xl font-semibold italic leading-tight text-neutral-50 sm:text-3xl" data-testid="offer-popup-title">
                {offer.title}
              </h2>

              <div className="mt-4 flex flex-wrap items-end gap-3">
                {offer.original_price ? (
                  <span className="text-sm text-neutral-500 line-through" data-testid="offer-popup-original-price">
                    {inr(offer.original_price)}
                  </span>
                ) : null}
                <span className="text-3xl font-bold tracking-tight text-goldlight" data-testid="offer-popup-offer-price">
                  {inr(offer.offer_price)}
                </span>
                {save ? (
                  <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1 font-mono text-[9px] tracking-[0.12em] text-emerald-300" data-testid="offer-popup-save">
                    YOU SAVE {inr(save)}
                  </span>
                ) : null}
              </div>

              {offer.description && (
                <p className="mt-4 text-sm leading-relaxed text-neutral-400" data-testid="offer-popup-description">
                  {offer.description}
                </p>
              )}

              {claimed ? (
                <div
                  className="mt-7 flex items-start gap-3 rounded-2xl border border-emerald-400/30 bg-emerald-400/[0.08] px-5 py-4"
                  data-testid="offer-popup-claimed"
                >
                  <BadgeCheck size={20} className="mt-0.5 shrink-0 text-emerald-300" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-200">Claim received!</p>
                    <p className="mt-1 text-xs leading-relaxed text-neutral-400">
                      Our team has been notified and will contact you shortly to close this deal.
                    </p>
                    <button
                      onClick={close}
                      data-testid="offer-popup-done"
                      className="mt-3 font-mono text-[10px] tracking-[0.2em] text-gold transition-colors hover:text-goldlight"
                    >
                      DONE →
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-7 space-y-3">
                  <button
                    onClick={claim}
                    disabled={busy}
                    data-testid="offer-popup-claim"
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-4 text-sm font-bold tracking-[0.14em] text-black transition-transform duration-300 hover:scale-[1.02] disabled:opacity-50"
                  >
                    {busy && <Loader2 size={15} className="animate-spin" />}
                    CLAIM THIS OFFER
                  </button>
                  <button
                    onClick={close}
                    data-testid="offer-popup-later"
                    className="w-full py-1 text-center font-mono text-[10px] tracking-[0.22em] text-neutral-500 transition-colors hover:text-neutral-300"
                  >
                    MAYBE LATER
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
